import * as React from 'react';
import './Calendar.css';

interface CalendarProps {
  selectedDate: Date | null;
  onDateSelect: (date: Date) => void;
  onClose: () => void;
  style?: React.CSSProperties;
  minSelectableDate?: Date | null;
  maxSelectableDate?: Date | null;
}

const Calendar = ({ 
  selectedDate, 
  onDateSelect, 
  onClose, 
  style, 
  minSelectableDate, 
  maxSelectableDate 
}: CalendarProps) => {
  // Stato per il mese e l'anno visualizzati
  const [viewDate, setViewDate] = React.useState(selectedDate || new Date());
  const currentMonth = viewDate.getMonth();
  const currentYear = viewDate.getFullYear();

  const oggi = new Date(); // Data di oggi per confronto

  const giorni = ['Lu', 'Ma', 'Me', 'Gi', 'Ve', 'Sa', 'Do'];
  const mesi = [
    'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
  ];

  const handlePrevMonth = () => {
    setViewDate(new Date(currentYear, currentMonth - 1, 1));
  };

  const handleNextMonth = () => {
    setViewDate(new Date(currentYear, currentMonth + 1, 1));
  };

  // Calcola i giorni del mese corrente
  const days: (Date | null)[] = [];
  const firstDay = new Date(currentYear, currentMonth, 1);
  const lastDay = new Date(currentYear, currentMonth + 1, 0);
  
  // Aggiungi i giorni vuoti all'inizio
  const firstDayOfWeek = firstDay.getDay() || 7;
  for (let i = 1; i < firstDayOfWeek; i++) {
    days.push(null);
  }
  
  // Aggiungi i giorni del mese
  for (let day = 1; day <= lastDay.getDate(); day++) {
    days.push(new Date(currentYear, currentMonth, day));
  }

  // Funzione per normalizzare una data a mezzanotte per confronti corretti
  const normalizeDate = (date: Date): Date => {
    const newDate = new Date(date);
    newDate.setHours(0, 0, 0, 0);
    return newDate;
  };

  return (
    <>
      <div className="calendar-overlay" onClick={onClose}>
        <div className="calendar" style={style} onClick={(e) => e.stopPropagation()}>
          <div className="calendar-header">
            <button onClick={handlePrevMonth}>
              <svg width="14" height="20" viewBox="0 0 14 20">
                <polygon points="11,2 11,18 3,10" />
              </svg>
            </button>
            <span>{mesi[currentMonth]} {currentYear}</span>
            <button className="right-arrow" onClick={handleNextMonth}>
              <svg width="14" height="20" viewBox="0 0 14 20">
                <polygon points="11,2 11,18 3,10" />
              </svg>
            </button>
          </div>
          <div className="calendar-days">
            {giorni.map(g => (
              <div key={g} className="calendar-day-name">{g}</div>
            ))}
          </div>
          <div className="calendar-grid">
            {days.map((date, index) => {
              let isDisabled = false;
              if (date) {
                const normalizedCurrentDate = normalizeDate(date);
                if (minSelectableDate) {
                  const normalizedMinDate = normalizeDate(minSelectableDate);
                  if (normalizedCurrentDate < normalizedMinDate) {
                    isDisabled = true;
                  }
                }
                if (maxSelectableDate && !isDisabled) {
                  const normalizedMaxDate = normalizeDate(maxSelectableDate);
                  if (normalizedCurrentDate > normalizedMaxDate) {
                    isDisabled = true;
                  }
                }
              }

              return (
                <button
                  key={index}
                  className={`calendar-day ${!date ? 'empty' : ''} ${isDisabled ? 'disabled-day' : ''} ${
                    date && selectedDate && 
                    date.getDate() === selectedDate.getDate() && 
                    date.getMonth() === selectedDate.getMonth() && 
                    date.getFullYear() === selectedDate.getFullYear() ? 'selected' : ''
                  } ${
                    date && 
                    date.getDate() === oggi.getDate() && 
                    date.getMonth() === oggi.getMonth() && 
                    date.getFullYear() === oggi.getFullYear() ? 'today' : ''
                  }`}
                  onClick={() => date && !isDisabled && onDateSelect(date)}
                  disabled={!date || isDisabled}
                >
                  {date ? date.getDate() : ''}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default Calendar; 